import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { SideNavItem } from './side-nav-item';
import useHousehold, { ExperienceFlag } from 'src/contexts/household-context';

interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

// First path segments that are static app routes, not household-member ud_ids
const STATIC_FIRST_SEGMENTS = new Set(['planning', 'account', 'express']);

const udIdFromItemPath = (path?: string): string | null => {
  if (!path) return null;
  const match = path.match(/^\/([^/]+)(?:\/|$)/);
  if (!match || STATIC_FIRST_SEGMENTS.has(match[1])) return null;
  return match[1];
};

const renderItems = ({
  depth = 0,
  items,
  pathname,
  experienceFlag,
  selectedUdId,
  setSelectedUdId,
  headOfHouseholdUdId,
  activeItemIndex = -1,
  isOpen = false
}: {
  depth?: number;
  items: Item[];
  pathname?: string | null;
  experienceFlag: ExperienceFlag;
  selectedUdId: string | null;
  setSelectedUdId: (udid: string) => void;
  headOfHouseholdUdId: string | null;
  activeItemIndex?: number;
  isOpen?: boolean;
}): JSX.Element[] => {

  const filteredItems = items.filter((item) => {
    return !(experienceFlag === ExperienceFlag.READONLY && item.title === 'Sharing');
  });

  return filteredItems.reduce(
    (acc: JSX.Element[], item, itemIndex) => reduceChildRoutes({
      acc,
      depth,
      item,
      pathname,
      experienceFlag,
      selectedUdId,
      setSelectedUdId,
      headOfHouseholdUdId,
      isActive: itemIndex === activeItemIndex,
      itemIndex,
      isOpen: depth === 0 && itemIndex === activeItemIndex ? isOpen : false
    }),
    []
  );
};

// Helper: does any child have the selected user (for opening sections)?
function hasSelectedChild(items: Item[], selectedUdId: string | null): boolean {
  return items.some(child => {
    if (child.items) {
      return hasSelectedChild(child.items, selectedUdId);
    }
    const udIdFromPath = udIdFromItemPath(child.path);
    return Boolean(udIdFromPath && selectedUdId && udIdFromPath === selectedUdId);
  });
}

const reduceChildRoutes = ({
  acc,
  depth,
  item, 
  pathname,
  experienceFlag,
  selectedUdId,
  setSelectedUdId,
  headOfHouseholdUdId,
  isActive = false,
  itemIndex,
  isOpen = false
}: {
  acc: JSX.Element[];
  depth: number;
  item: Item;
  pathname?: string | null;
  experienceFlag: ExperienceFlag;
  selectedUdId: string | null;
  setSelectedUdId: (udid: string) => void;
  headOfHouseholdUdId: string | null;
  isActive?: boolean;
  itemIndex: number;
  isOpen?: boolean;
}): Array<JSX.Element> => {

  if (experienceFlag === ExperienceFlag.READONLY && item.title === 'Sharing') {
    return acc;
  }

  // Extract ud_id from path if present (null for static routes like /planning/*)
  const udIdFromPath = udIdFromItemPath(item.path);
  const isSelected = Boolean(udIdFromPath && selectedUdId && udIdFromPath === selectedUdId);

  // Check if current path matches this item's path (for individual items)
  const isPathMatch = item.path && pathname && (
    pathname === item.path || 
    (pathname.startsWith(item.path + '/') || pathname.startsWith(item.path + '?'))
  );


  if (item.items) {
    // For section headers, use the passed-in isOpen state
    acc.push(
      <SideNavItem
        active={isActive}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={item.title}
        label={item.label}
        open={depth === 0 ? isOpen : (isActive || hasSelectedChild(item.items, selectedUdId) || Boolean(pathname && item.items?.some(child =>
          child.path && (
            pathname === child.path ||
            pathname.startsWith(child.path + '/') ||
            pathname.startsWith(child.path + '?')
          )
        )))}
        title={item.title}
        onClick={udIdFromPath ? () => setSelectedUdId(udIdFromPath!) : undefined}
      >
        <Stack
          component="ul"
          spacing={0}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
            experienceFlag,
            selectedUdId,
            setSelectedUdId,
            headOfHouseholdUdId,
            activeItemIndex: -1 // Don't restrict child items, let them determine their own active state
          })}
        </Stack>
      </SideNavItem>
    );
  } else {
    acc.push(
      <SideNavItem
        active={Boolean(isPathMatch && (!udIdFromPath || isSelected))}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={item.label}
        path={item.path}
        title={item.title}
        onClick={udIdFromPath ? () => setSelectedUdId(udIdFromPath!) : undefined}
      />
    );
  }

  return acc;
};

interface SideNavSectionProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
  activeItemIndex?: number;
  isOpen?: boolean;
  selectedUdId?: string | null;
}

export const SideNavSection: FC<SideNavSectionProps> = (props) => {
  const { 
    items = [], 
    pathname, 
    subheader = '', 
    activeItemIndex = -1,
    isOpen = false,
    selectedUdId,
    ...other 
  } = props;

  const { experienceFlag, setSelectedUdId, selectedHousehold } = useHousehold();
  const headOfHouseholdUdId = selectedHousehold?.head_of_household?.ud_id || null;

  return (
    <Stack
      component="ul"
      spacing={0}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0
      }}
      {...other}
    >
      {subheader && (
        <Box
          component="li"
          sx={{
            color: 'var(--nav-section-title-color)',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.66,
            mb: 0.3,
            pl: 1,
            mt: 1,
            textTransform: 'none'
          }}
        >
          {subheader}
        </Box>
      )}
      {renderItems({ items, pathname, experienceFlag, selectedUdId: selectedUdId || null, setSelectedUdId, headOfHouseholdUdId, activeItemIndex, isOpen })}
    </Stack>
  );
};

SideNavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  subheader: PropTypes.string,
};
